import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  useTheme,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Close,
  ChevronLeft,
  ChevronRight,
  CameraAlt,
  Phone,
  Person,
  AccessTime
} from '@mui/icons-material';
import moment from 'moment';
import axios from '../../resources/axios';
import { Contract } from '../../resources/types/contractsTypes';

interface Props {
  open: boolean;
  onClose: () => void;
  scooterId: string;
  scooterName?: string;
}

const TripPhotosModal: React.FC<Props> = ({ open, onClose, scooterId, scooterName }) => {
  const theme = useTheme();
  const [trips, setTrips] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open || !scooterId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get('/contracts/active', {
          params: { scooterId, active: false, limit: 20, page: 1 }
        });
        if (cancelled) return;
        const items: Contract[] = response.data?.contracts || [];
        const withPhotos = items
          .filter(t => t.cableImg)
          .sort((a, b) => {
            const ad = a.closedDate || a.updatedAt;
            const bd = b.closedDate || b.updatedAt;
            return moment(bd).valueOf() - moment(ad).valueOf();
          });
        setTrips(withPhotos);
        setIndex(0);
      } catch (e) {
        console.error('Failed to load trip photos', e);
        if (!cancelled) setError('Не вдалося завантажити фото поїздок');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, scooterId]);

  const current = trips[index];

  const fullName = useMemo(() => {
    if (!current?.user) return '—';
    const { firstName, lastName } = current.user;
    return [firstName, lastName].filter(Boolean).join(' ').trim() || '—';
  }, [current]);

  const handlePrev = useCallback(() => {
    setIndex(i => Math.min(trips.length - 1, i + 1));
  }, [trips.length]);

  const handleNext = useCallback(() => {
    setIndex(i => Math.max(0, i - 1));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: 'rgba(78, 205, 196, 0.15)', color: '#4ECDC4',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <CameraAlt />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Фото поїздок
            </Typography>
            {scooterName && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Самокат №{scooterName}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#4ECDC4' }} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: 'center', color: '#F44336' }}>
            <Typography>{error}</Typography>
          </Box>
        ) : trips.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
            <CameraAlt sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography>Для цього самоката ще немає фото поїздок</Typography>
          </Box>
        ) : current ? (
          <Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F0F0F0',
              minHeight: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={current.cableImg}
                alt="Trip"
                style={{
                  maxWidth: '100%',
                  maxHeight: 480,
                  display: 'block',
                  objectFit: 'contain'
                }}
              />

              {trips.length > 1 && (
                <>
                  <Tooltip title="Попередня поїздка (старіша)">
                    <span>
                      <IconButton
                        onClick={handlePrev}
                        disabled={index >= trips.length - 1}
                        sx={{
                          position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.5)', color: '#FFF',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                          '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.3)' }
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Наступна поїздка (новіша)">
                    <span>
                      <IconButton
                        onClick={handleNext}
                        disabled={index <= 0}
                        sx={{
                          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.5)', color: '#FFF',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                          '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.3)' }
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Box sx={{
                    position: 'absolute', top: 8, right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)', color: '#FFF',
                    px: 1.5, py: 0.5, borderRadius: 2,
                    fontSize: 12, fontWeight: 500
                  }}>
                    {index + 1} / {trips.length}
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{
              mt: 2, p: 2, borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
            }}>
              <Avatar sx={{ bgcolor: 'rgba(78, 205, 196, 0.2)', color: '#4ECDC4' }}>
                <Person />
              </Avatar>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                  Клієнт
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {fullName}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ color: '#4ECDC4', fontSize: 18 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Телефон
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {current.user?.phone
                      ? <a href={`tel:${current.user.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{current.user.phone}</a>
                      : '—'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime sx={{ color: '#4ECDC4', fontSize: 18 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Час поїздки
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {moment(current.closedDate || current.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {trips.length > 1 && (
              <Box sx={{
                mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 1
              }}>
                {trips.map((t, i) => (
                  <Box
                    key={t._id}
                    onClick={() => setIndex(i)}
                    sx={{
                      width: 64, height: 64, flexShrink: 0,
                      borderRadius: 1, overflow: 'hidden',
                      cursor: 'pointer',
                      border: i === index ? '2px solid #4ECDC4' : `2px solid ${theme.palette.divider}`,
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: '#7EDDD6' }
                    }}
                  >
                    <img
                      src={t.cableImg}
                      alt={`Trip ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default TripPhotosModal;
